import React, { useCallback, useContext, useEffect, useState } from 'react';
import { EllipsisOutlined, ExclamationCircleOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Dropdown, Layout, Menu, Modal, PageHeader, Table, Typography } from 'antd';
import { apiClient, apiErrorMessage, hasPermission } from '../../../utils';
import { City } from '../../../models/index';
import { UserContext } from '../../contexts';
import { CityEditPage } from './city-edit-page';
import { EntityChangeLog } from '../../reports';

export function CitiesPage() {
  const { Content } = Layout;
  const { Text } = Typography;

  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState<City[]>();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalCity, setEditModalCity] = useState<City>();
  const [canEdit, setCanEdit] = useState(false);
  const [changeLogModalOpen, setChangeLogModalOpen] = useState<string>();

  useEffect(() => {
    setCanEdit(hasPermission(user, 'countries.edit'));
  }, [user]);

  const loadCities = useCallback(() => {
    setLoading(true);
    apiClient
      .get(`cities`)
      .then((response: any) => {
        setCities(response.data);
        setLoading(false);
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  }, []);

  useEffect(() => {
    loadCities();
  }, [loadCities]);

  const onRefreshClick = () => {
    loadCities();
  };

  const onCityEditSaved = () => {
    closeEditModal();
    loadCities();
  };

  const openEditModal = () => {
    setEditModalCity(undefined);
    setEditModalOpen(true);
  };

  const openEditModalByCity = (city: City) => {
    setEditModalCity(city);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditModalCity(undefined);
  };

  const openDetailModalByCity = (city: City) => {
    openEditModalByCity(city);
  };

  const deleteCity = (id: string) => {
    setLoading(true);
    apiClient
      .delete(`cities/${id}`)
      .then(() => {
        setLoading(false);
        loadCities();
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const confirmDeleteCity = (id: string) => {
    const onOk = () => deleteCity(id);
    Modal.confirm({
      title: 'Şehir sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Şehir silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: onOk,
    });
  };

  const openChangeLogModal = (id: string) => {
    setChangeLogModalOpen(id);
  };

  const onTableActionClick = (e: any, id: string) => {
    if (e.key === 'changelog') {
      openChangeLogModal(id);
    }
    if (e.key === 'edit') {
      const city = cities?.find(m => m.id === id);
      if (city) {
        openEditModalByCity(city);
      } else {
        openEditModal();
      }
    } else if (e.key === 'del') {
      confirmDeleteCity(id);
    }
  };

  const closeChangeLogModal = () => {
    setChangeLogModalOpen(undefined);
  };

  const tableActions = (id: string) => (
    <Menu onClick={(e: any) => onTableActionClick(e, id)}>
      <Menu.Item key="changelog">
        <Text>Değişim geçmişi</Text>
      </Menu.Item>
      {canEdit && (
        <>
          <Menu.Item key="edit">
            <Text>Düzenle</Text>
          </Menu.Item>
          <Menu.Item key="del">
            <Text type="danger">Sil</Text>
          </Menu.Item>
        </>
      )}
    </Menu>
  );

  const tableColumns: any[] = [
    {
      title: 'İsim',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, city: City) => (
        <Button type="link" onClick={() => openDetailModalByCity(city)}>
          {name}
        </Button>
      ),
    },
    {
      title: 'Ülke',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, city: City) => city.country?.name,
    },
    {
      title: '',
      key: 'action',
      render: (_: any, city: City) => (
        <div style={{ textAlign: 'right' }}>
          <Dropdown overlay={tableActions(city.id)} trigger={['click']}>
            <Button size="small" icon={<EllipsisOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title=""
        extra={[
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
          <>
            {canEdit && (
              <Button key="add" onClick={openEditModal} icon={<PlusOutlined />} type="primary">
                Yeni Şehir
              </Button>
            )}
          </>,
        ]}
      />
      <Content>
        <Table columns={tableColumns} dataSource={cities} loading={loading} pagination={false} />
      </Content>
      <Modal
        title="Değişim geçmişi"
        visible={changeLogModalOpen !== undefined}
        footer={null}
        onCancel={closeChangeLogModal}
        destroyOnClose={true}
        width={'80%'}
      >
        <EntityChangeLog entityName={'City'} recordId={changeLogModalOpen} />
      </Modal>

      <Modal title="Şehir" visible={editModalOpen} footer={null} onCancel={closeEditModal} destroyOnClose={true}>
        <CityEditPage city={editModalCity} onSaved={onCityEditSaved} onCancel={closeEditModal} />
      </Modal>
    </>
  );
}
